/**
 * @file     core.ts
 * @readme   ../../CLAUDE.md
 * @purpose  The English namespaces every page can need (common, nav, forms, flows, marketing, map). The browser i18n instance starts with this; workspace namespaces register on demand (see ../../deferred).
 */
import { common, error, footer, notFound } from './common';
import { components } from './components';
import { dealFinder } from './dealFinder';
import { findTenantsFlow } from './findTenantsFlow';
import { forms } from './forms';
import { home } from './home';
import { homeValueFlow } from './homeValueFlow';
import { ctas, pillars, sections, topActions } from './marketing';
import { nav } from './nav';
import { shared } from './shared';
import { mortgageCalculatorFlow } from './mortgageCalculatorFlow';
import { preApprovalFlow } from './preApprovalFlow';
import { buyerMatchFlow, leadFlow } from './buyerMatchFlow';
import { presaleFlow } from './presaleFlow';
import { rateComparison } from './rateComparison';
import { unifiedLeadFlow } from './unifiedLeadFlow';
import { welcomeBack } from './welcomeBack';
import { legal } from './legal';
import { siteNotice } from './siteNotice';
import { prompts } from './prompts';

export const enCore = {
  common,
  components,
  error,
  notFound,
  nav,
  footer,
  forms,
  findTenantsFlow,
  sections,
  ctas,
  topActions,
  pillars,
  home,
  dealFinder,
  homeValueFlow,
  preApprovalFlow,
  buyerMatchFlow,
  presaleFlow,
  leadFlow,
  rateComparison,
  legal,
  mortgageCalculatorFlow,
  shared,
  unifiedLeadFlow,
  welcomeBack,
  siteNotice,
  prompts,
  // Interactive-map strings. `map.transit` is an OBJECT node (legend title +
  // mode names); the chip rail uses the sibling leaf `transitLabel` so the
  // two lookups can't collide.
  pages: {
    properties: {
      map: {
        transitLabel: 'Transit',
        transit: { title: 'Transit', train: 'Train', bus: 'Bus', ferry: 'Ferry' },
        parks: 'Parks',
        postalAreas: 'Postal areas',
        schools: 'Schools',
        density: 'Density',
        moreLayers: 'More',
        options: {
          title: 'Map options',
          close: 'Close map options',
          baseMap: 'Base map',
          standard: 'Map',
          satellite: 'Satellite',
          places: 'Places',
          boundaries: 'Boundaries',
          neighbourhoods: 'Neighbourhoods',
          data: 'Data layers',
          drawing: 'Drawing',
        },
        listingStatus: 'Map listing status',
        sold: 'Sold',
        active: 'Active',
        overlayInfo: {
          schools: 'The heat layer shows access to current public and independent schools across British Columbia. Pin colours show available Fraser Institute scores; unrated schools use a neutral pin.',
          density: 'Population density from Statistics Canada 2021 Census, normalized per km². Sub-municipal distribution estimated from census tract data.',
        },
        satelliteOn: 'Switch to satellite view',
        satelliteOff: 'Switch to map view',
        findLocation: 'Find your location',
        findLocationAria: 'Find your location on the map',
        draw: {
          start: 'Draw search area',
          cancel: 'Cancel draw',
          title: 'Draw an area',
          instructions: 'Click points. Double-click to finish.',
          instructionsTouch: 'Tap points. Double-tap to finish.',
          cancelHint: 'Press the pencil button to cancel',
          cancelAction: 'Cancel',
        },
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        recenter: 'Recenter',
        popup: {
          independent: 'Independent',
          school: 'School',
          score: 'Score {{score}}/10',
          unrated: 'Unrated',
          line: '{{line}} Line',
        },
        sheet: {
          loading: 'Loading listing details',
          loadFailed: 'This listing did not load',
          retry: 'Try again',
          openFullPage: 'Open full page',
          dragToDismiss: 'Drag to dismiss listing details',
        },
        panel: {
          mode: 'Results panel mode',
          loading: 'Loading listings',
          listingsInView: 'Listings in view',
          listingsInArea: 'Listings in {{area}}',
          describeHome: 'Describe a home',
        },
      },
    },
    neighborhoodGuides: {
      map: {
        ariaLabel: 'Interactive map of Greater Vancouver',
        loadError: 'The map could not load. Please refresh to try again.',
      },
    },
  },
} as const;
